/**
 * The sales-order list — `/countries/:cc/sales-orders`. READ-ONLY, and that is
 * structural rather than a policy: the API ships `GET /countries/:cc/sales-orders`
 * and `GET /sales-orders/:id` and NO SO write surface of any kind. An order comes
 * into existence one way — somebody confirms a quote — so this screen has no
 * edit and no status control, because neither has an endpoint behind it.
 *
 * =============================================================================
 * ⚠ IT DOES NOW HAVE A CREATE BUTTON, AND THAT SENTENCE ABOVE IS STILL TRUE
 * =============================================================================
 * This header used to end "…so this screen has no create button", and the read-only
 * test pinned the absence of one. The owner's ask was to be able to raise an order
 * without first raising and sending a quote; the answer was NOT an SO write route —
 * it is that `/quotes/:id/confirm` now accepts a `draft` (`quote-lifecycle.service.ts`
 * §2, OQ-U3-1). So "New sales order" is a link to the QUOTE BUILDER in order mode,
 * which writes a quote and confirms it. Every claim this file makes about there being
 * no SO write endpoint survives intact, and `sales-orders.e2e-spec.ts` still pins that
 * none of `POST /countries/:cc/sales-orders`, `PATCH /sales-orders/:id` or the Phase 3b
 * agent routes exists.
 *
 * IT IS GATED ON THE TWO KEYS THE FLOW ACTUALLY USES — `quotes.create` AND
 * `quotes.confirm` — and not on `so.view`, which the reader of this list already holds.
 * A caller holding only one of the pair is shown nothing, because the D5 rule is that a
 * control the caller cannot complete is hidden rather than offered and then refused.
 *
 * =============================================================================
 * `pending_push` IS SHOWN HONESTLY. THIS IS THE POINT OF THE SCREEN.
 * =============================================================================
 * Every order this build can produce sits at `pending_push`, waiting for a Tally
 * write path that is Phase 3b and does not exist. Three ways to render that would
 * all have been lies:
 *
 *  · AS AN ERROR (red, "failed to sync"). Nothing failed. No push was attempted,
 *    because no push code exists.
 *  · AS A SPINNER, or any "syncing…" progress treatment. That says work is
 *    happening. No process is running, no queue is draining, and a spinner that
 *    never resolves is the single most expensive UI lie available here — somebody
 *    would sit and wait for it.
 *  · AS A NEUTRAL BLANK. That hides the fact that the order has NOT reached
 *    Tally, which is exactly what an accountant needs to know before they go
 *    looking for the voucher.
 *
 * So it is `status/info` blue, labelled "Awaiting Tally sync", with a standing
 * banner naming the phase. `domain/quotes.ts` holds the tone; the banner is here.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, CountryBadge } from '../../components/ui';
import { SALES_ORDER_STATUS_MAP, isSalesOrderStatus } from '../../domain/quotes';
import type { SalesOrderStatus } from '../../domain/quotes';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { ServerAmount } from './money';
import { SalesOrderStatusPill } from './status';
import { DocumentSkeletonTable, OutOfRangeNotice, SalesOrdersEmptyState } from './states';
import { listAnswer } from './list-answer';
import { quoteErrorText } from './errors';
import { useSalesOrderCountryTotal, useSalesOrderList } from './use-quotes';

const PER_PAGE = 50;

/**
 * This same list at page 1 — every other search parameter kept, `page` dropped.
 *
 * Built here rather than in `states.tsx` for the reason `InquiryEmptyState`
 * already records for `hrefForStage`: the URL contract belongs to the screen that
 * owns the route, so a notice cannot invent a path this page does not serve.
 */
function firstPageHref(countryCode: string, params: URLSearchParams): string {
  const patch = new URLSearchParams(params);
  patch.delete('page');
  const query = patch.toString();
  return `/countries/${countryCode}/sales-orders${query === '' ? '' : `?${query}`}`;
}

export function SalesOrdersListPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'so.view');
  const canViewQuotes = can(countryCode, 'quotes.view');
  /*
   * BOTH KEYS, because the flow performs both acts — see the header. `&&` and not
   * `||`: a seat that can create quotes but not confirm them would be offered a
   * screen whose last click is a 403, which is the shape D5 exists to prevent.
   */
  const canPlaceOrder =
    can(countryCode, 'quotes.create') && can(countryCode, 'quotes.confirm');

  const rawStatus = params.get('status');
  const status: SalesOrderStatus | null = isSalesOrderStatus(rawStatus) ? rawStatus : null;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const list = useSalesOrderList(countryCode, { status, page, perPage: PER_PAGE }, canView);
  /*
   * W2 — SEPARATE FROM THE LIST, BECAUSE THE LIST IS FILTERED.
   *
   * `list.data.total` counts the ACTIVE TAB. Using it for "N orders in IN" and
   * for the empty card's "there are N altogether" told a reader on the Pushed tab
   * that the country held zero orders while a `pending_push` one sat one tab
   * away. `QuotesListPage` has always passed the unfiltered count for exactly
   * this reason; this is that, matched.
   */
  const countryTotal = useSalesOrderCountryTotal(countryCode, canView);

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Sales orders are not available to this account">
          Viewing sales orders in {countryCode} needs the Sales Orders permission.
        </Alert>
      </div>
    );
  }

  /*
   * ===========================================================================
   * V4 — ONE PAYLOAD PER RENDER. THE TWIN OF `QuotesListPage`, SAME ROOT CAUSE.
   * ===========================================================================
   * `useSalesOrderList` carries `placeholderData: keepPreviousData`, so between a
   * click and its answer `list.data` is the PREVIOUS response while `page` and
   * `status` above are already the NEW url. Clicking the "Pushed to Tally" filter
   * from `?page=3` — the handler drops `page`, so the URL page is 1 — rendered
   * "Page 1 is past the end of this list" over "This list ends at page 2": page 1
   * of a list is not past its end, and "this list" was by then a different tab
   * whose total is not 60. This screen has NO pager, so that notice's link is the
   * only exit it offers, and it pointed at the page already on screen.
   *
   * The response says which page it describes (`{data, page, perPage, total}`), so
   * everything the notice states is read off `shown` and cannot disagree WITH
   * ITSELF. `page`/`PER_PAGE` above remain the REQUEST — the query key must ask
   * for what the URL says — and nothing renders off the request.
   *
   * ===========================================================================
   * WHAT ONE-PAYLOAD DOES NOT BUY. READ THIS BEFORE ATTEMPTING A SIXTH FIX.
   * ===========================================================================
   * An earlier draft of this block said everything here "cannot disagree with
   * itself" and left the reader to infer that nothing on screen could disagree at
   * all. That inference was FALSE, and a comment that invites it is the thing
   * that makes the next reader stop looking.
   *
   * Self-consistency is a property of the ANSWER, and says nothing about whether
   * the answer is to the question being asked. Under `keepPreviousData` it
   * routinely is not: the filter strip is driven by `status` off the URL and the
   * rows are driven by the payload, and for one round trip those are two different
   * requests. That gap is not closed by reading one object — it is closed by
   * MARKING, below.
   *
   * ===========================================================================
   * V6 — NOR DOES IT SAY WHO MAY QUOTE THE PAYLOAD ONCE THE ROWS ARE GONE.
   * ===========================================================================
   * The Phase-3b banner below counts `pending_push` rows and calls them "the
   * orders below". It was rendered outside the branch that decides what is below,
   * and read the count off a page-scope `rows` — so an errored refetch (reachable
   * in production: `lib/query-client.ts` never retries a 4xx) produced, verbatim,
   *
   *   "Sales orders137 orders in INOrders are not yet pushed to TallyThe Tally
   *    write path is Phase 3b. 2 of the orders below are recorded here and have
   *    not been sent anywhere — no push has been attempted and none is running.
   *    The voucher will not exist in Tally until that phase ships.AllAwaiting
   *    Tally syncPushed to TallyInvoicedCancelledThe sales-order list could not be
   *    loadedSomething went wrong. Nothing was changed."
   *
   * — with no orders below at all. `behind` did not save it: `isPlaceholderData`
   * is false in the error state, so the banner was at full opacity, and
   * `list.data` SURVIVES an errored refetch, so the count was still two.
   *
   * `listAnswer` removes the page-scope payload entirely. `rows` exists only on
   * the variant that has rows, so `pendingCount` below cannot be taken without
   * first saying which answer it is counting — and the same is true of a seventh
   * node nobody has written yet. `list-answer.ts` carries the full argument; this
   * screen and `QuotesListPage` are its two callers and share its guarantee.
   *
   * ===========================================================================
   * V5 — THE ROWS ARE VISIBLY BEHIND, NOT PRESENTED AS THE SELECTION'S ANSWER.
   * ===========================================================================
   * The twin of `QuotesListPage`'s `behind`, same root cause and same treatment;
   * that file carries the full argument, including why this is `isPlaceholderData`
   * and NOT `isFetching`, and why the filter strip is deliberately left alone.
   *
   * Reproduced here: from the All tab with two orders on screen, clicking "Pushed
   * to Tally" left `aria-selected="true"` on Pushed over two rows pilled "Awaiting
   * Tally sync", under a standing banner counting those two — and nothing marked
   * the screen busy.
   *
   * V4, second half — IN ADDITION to reading one payload, never instead of it.
   * `keepPreviousData` exists so a TABLE does not blank for a round trip; a held
   * payload with no rows has no table to keep. Holding it kept a notice about a
   * result the reader had left, and would hand `SalesOrdersEmptyState` a
   * `statusLabel` taken from the URL — the tab now being fetched — to describe the
   * old tab's emptiness. That case is the `loading` variant now, which is what
   * makes the URL label safe on the two empty variants: neither can be a
   * placeholder, so the tab the URL names is the tab the payload answers.
   *
   * PAGING DIVIDES THE FILTERED TOTAL, and `answer.shown.total` is that — the
   * ACTIVE TAB's own count. Deliberately not `inCountry`: "is there a page 3?" is
   * a question about the result set this screen asked for. `inCountry` answers a
   * different question (W2) and using it here would call a full Pushed tab
   * out-of-range because the country happens to hold more orders.
   */
  const answer = listAnswer(list);
  const behind = answer.kind === 'rows' && answer.behind;
  /*
   * V1 — `?? null`, NOT `?? 0`. The two queries are separate round trips and the
   * filtered one is routinely the first back, so this value is genuinely UNKNOWN
   * for a window on every load. Coercing it to zero there handed the empty card a
   * country count of 0 and put "No sales orders yet" on screen while the country
   * held orders — W2's sentence, surviving inside the loading window. `null` says
   * "not known"; `states.tsx` renders that as a claim about the tab and no claim
   * about the country, and `QuotesListPage`'s tabs make the same distinction.
   */
  const inCountry = countryTotal.data ?? null;
  /*
   * V6 — THE COUNT IS A CLAIM ABOUT THE ROWS, SO IT IS TAKEN FROM THE ANSWER THAT
   * HAS ROWS AND FROM NOWHERE ELSE.
   *
   * `answer.rows` is unreadable until `kind` is narrowed, so this line cannot be
   * written any other way — which is the whole of the fix. Every state without
   * rows on screen counts zero, and zero drops the banner, so a banner that says
   * "the orders below" can no longer outlive them.
   */
  const pendingCount =
    answer.kind === 'rows'
      ? answer.rows.filter((row) => row.status === 'pending_push').length
      : 0;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Sales orders"
        subtitle={
          inCountry === null
            ? undefined
            : `${String(inCountry)} ${inCountry === 1 ? 'order' : 'orders'} in ${countryCode}`
        }
        actions={
          canPlaceOrder ? (
            <Link
              to={`/countries/${countryCode}/sales-orders/new`}
              className="inline-flex h-9 items-center gap-2 rounded-sm bg-indigo px-4 text-body font-medium text-fg-inverse hover:bg-indigo/90"
            >
              <Plus aria-hidden="true" className="size-4" />
              New sales order
            </Link>
          ) : undefined
        }
      />

      {/*
       * The standing statement. `tone="info"`, not warning: nothing is wrong. It
       * renders whenever this page is showing at least one order that has not been
       * pushed — which today is all of them — and it names the phase so a reader
       * knows this is a not-yet, not a failure.
       *
       * V5 — IT COUNTS THE HELD ROWS, SO IT DIMS WITH THEM. `pendingCount` is
       * computed from the rows, and the sentence says "the orders below": while
       * `behind` those are the previous filter's orders, and a full-opacity banner
       * over a dimmed table reads as the one part of the screen that IS current.
       * It is marked rather than suppressed — the statement stays true of the rows
       * it is counting, and hiding a standing truth for a round trip is the kind of
       * flicker this file's header argues against.
       *
       * It carries the dim and NOT `aria-busy`: this is `role="alert"`, announced
       * once when it appears, and it neither changes nor re-asserts during the
       * window. The `aria-busy` that names the state belongs on the answer region
       * below, where the sentence that describes it also lives.
       *
       * V6 — AND WHEN THERE ARE NO ROWS BELOW IT IS NOT HERE AT ALL. Its position
       * above the filter strip is unchanged, deliberately: the rule is about what
       * DECIDES a node, not about where it sits. `pendingCount` is taken from the
       * answer and from nothing else, so this banner cannot render over a skeleton
       * or over a refusal — the dim was never going to reach those, because
       * `isPlaceholderData` is false in both.
       */}
      {pendingCount > 0 ? (
        <Alert
          tone="info"
          title="Orders are not yet pushed to Tally"
          className={cn(
            'motion-safe:transition-opacity motion-safe:duration-settle',
            behind && 'opacity-60',
          )}
        >
          {/*
            * W5. The plural was baked in — "1 of the orders below are recorded here",
            * which is the FIRST thing anybody sees, because the first confirmed quote
            * in a country produces exactly one order. Both branches are written out
            * rather than assembled from fragments: this sentence has to read as
            * English in the state a pilot actually starts in.
            */}
          The Tally write path is Phase 3b.{' '}
          {pendingCount === 1
            ? 'The order below is recorded here and has not been sent anywhere'
            : `${String(pendingCount)} of the orders below are recorded here and have not been sent anywhere`}{' '}
          — no push has been attempted and none is running. The voucher will not exist in
          Tally until that phase ships.
        </Alert>
      ) : null}

      <StatusFilter
        active={status}
        onSelect={(next) => {
          const patch = new URLSearchParams(params);
          if (next === null) patch.delete('status');
          else patch.set('status', next);
          patch.delete('page');
          setParams(patch, { replace: true });
        }}
      />

      {answer.kind === 'loading' ? (
        <DocumentSkeletonTable label="Loading sales orders" />
      ) : answer.kind === 'failed' ? (
        <Alert tone="danger" title="The sales-order list could not be loaded">
          {quoteErrorText(answer.error)}
        </Alert>
      ) : (
        /*
         * V5 — THE ANSWER REGION. Everything inside is read off the one variant
         * that got this far, so all of it carries `behind` at once, in
         * `TotalsBlock`'s own two classes: opacity only, 150ms, inside
         * `motion-safe` (hard rule 4). The twin of `QuotesListPage`'s region,
         * minus the pager this screen does not have.
         */
        <div
          aria-busy={behind}
          className={cn(
            'flex flex-col gap-4',
            'motion-safe:transition-opacity motion-safe:duration-settle',
            behind && 'opacity-60',
          )}
        >
          {/*
           * W1's "the caption stops asserting freshness", in a sentence. `behind`
           * implies rows on screen — it is true only on the `rows` variant, and a
           * rows-less placeholder is `loading` — so it only ever sits over a
           * table, and the wording matches the quote list's exactly: two screens
           * with one defect should not teach two vocabularies.
           */}
          {behind ? (
            <p className="text-label text-fg-secondary">
              These rows are the previous result — the list you asked for has not answered
              yet.
            </p>
          ) : null}

          {answer.kind === 'pastEnd' ? (
            /*
             * V3 — AN EMPTY PAGE IS NOT AN EMPTY RESULT SET.
             *
             * `rows.length === 0` alone sent `?status=pushed&page=3` of a 60-row
             * result to the empty card, which then rendered "No orders are pushed
             * to Tally — there are 60 in IN altogether." — denying, in its own
             * sentence, the count it was quoting. The condition that separates the
             * two is the FILTERED total: `> 0` means the result set has rows and
             * this page is simply past their end, which is navigation and not
             * emptiness. `states.tsx` holds why that gets a notice, not a card.
             *
             * V4 — the notice is a statement ABOUT A PAYLOAD, so all three numbers
             * are that payload's. V6 makes that structural: `pastEnd` is the only
             * variant that reaches this branch and `shown` exists only where a
             * payload landed, so there is no spelling of this node that could take
             * a page number off the URL. `firstPageHref` is the one prop that
             * stays on the URL, because it BUILDS a URL — "this address minus
             * `page`" is a fact about the address bar, not about the response, and
             * on a screen with no pager it is the only way out.
             */
            <OutOfRangeNotice
              page={answer.shown.page}
              total={answer.shown.total}
              perPage={answer.shown.perPage}
              firstPageHref={firstPageHref(countryCode, params)}
            />
          ) : answer.kind === 'empty' ? (
            <SalesOrdersEmptyState
              countryCode={countryCode}
              status={status}
              statusLabel={status === null ? 'All' : SALES_ORDER_STATUS_MAP[status].label}
              totalInCountry={inCountry}
              canViewQuotes={canViewQuotes}
              canPlaceOrder={canPlaceOrder}
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line bg-surface">
              <table className="w-full min-w-[760px] border-collapse">
                <caption className="sr-only">Sales orders in {countryCode}</caption>
                <thead>
                  <tr className="border-b border-line">
                    <Th>Number</Th>
                    <Th>Customer</Th>
                    <Th>From quote</Th>
                    <Th>Status</Th>
                    <Th align="right">Grand total</Th>
                    <Th>Created</Th>
                  </tr>
                </thead>
                <tbody>
                  {answer.rows.map((row) => (
                    <tr key={row.id} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
                      {/* PHASE 3a — the badge leads the identity column, the same
                        * position it holds on the quote table beside it. */}
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5">
                          <CountryBadge code={countryCode} />
                          <Link
                            to={`/countries/${countryCode}/sales-orders/${row.id}`}
                            className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                          >
                            {row.doc_no}
                          </Link>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-body13 text-fg">
                        {row.party_name ?? 'Unknown customer'}
                      </td>
                      <td className="px-4 py-3 text-body13">
                        {canViewQuotes ? (
                          <Link
                            to={`/countries/${countryCode}/quotes/${row.quote_id}`}
                            className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                          >
                            {row.quote_doc_no ?? row.quote_id}
                          </Link>
                        ) : (
                          <span className="font-mono text-mono text-fg-secondary">
                            {row.quote_doc_no ?? '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <SalesOrderStatusPill status={row.status} />
                      </td>
                      <td className="px-4 py-3 text-right text-body13 font-medium text-fg">
                        <ServerAmount value={row.grand_total} currency={row.currency} />
                      </td>
                      <td className="px-4 py-3 text-body13 text-fg-secondary">
                        {formatDateLong(row.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}): React.JSX.Element {
  return (
    <th
      scope="col"
      className={`px-4 py-2.5 text-th uppercase text-fg-secondary ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

/**
 * The status filter.
 *
 * All four enum members are offered even though only `pending_push` is reachable
 * today. Hiding the other three would make the screen silently unable to show a
 * Phase 3b order the moment one exists, and the filter is a URL parameter the API
 * validates anyway — an unknown value is refused there (422), not silently
 * dropped.
 */
function StatusFilter({
  active,
  onSelect,
}: {
  active: SalesOrderStatus | null;
  onSelect: (status: SalesOrderStatus | null) => void;
}): React.JSX.Element {
  const options: { key: SalesOrderStatus | null; label: string }[] = [
    { key: null, label: 'All' },
    ...(Object.keys(SALES_ORDER_STATUS_MAP) as SalesOrderStatus[]).map((status) => ({
      key: status,
      label: SALES_ORDER_STATUS_MAP[status].label,
    })),
  ];
  return (
    <div
      role="tablist"
      aria-label="Sales order status"
      className="flex w-fit max-w-full items-center gap-1 overflow-x-auto rounded-sm border border-line bg-subtle p-1"
    >
      {options.map((option) => {
        const selected = option.key === active;
        return (
          <button
            key={option.key ?? 'all'}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(option.key)}
            className={
              selected
                ? 'whitespace-nowrap rounded-sm bg-surface px-3 py-1.5 text-body font-medium text-fg shadow-tab-active'
                : 'whitespace-nowrap rounded-sm px-3 py-1.5 text-body text-fg-secondary hover:text-fg'
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
